import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  GraduationCap, UsersThree, MapPin, ArrowLeft, WhatsappLogo, CalendarPlus,
  UserCircle, ChalkboardTeacher, CircleNotch,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { SiteDiscussion } from "@/components/SiteDiscussion";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { EDITION } from "@/lib/edition";

const THEME = { surface: "#ffffff", text: "#1a2b25", primary: "#1f3d33" };

export default function BatchRoom() {
  const { instId, year } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/institutions/${instId}/batch/${year}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [instId, year]);

  const instName = data?.institution?.name || "";
  useSeo(
    data ? `Class of ${year} — ${instName}` : "Batch Room",
    data ? `Reconnect with the Class of ${year} at ${instName}. ${data.member_count} classmates, teachers of the era, shared memories and reunion planning.` : null,
    null,
    { canonicalPath: `/institutions/${instId}/batch/${year}` },
  );

  if (err) return (
    <div className="mx-auto max-w-3xl px-5 py-16 text-center" data-testid="batch-room-error">
      <p className="text-muted-foreground">We couldn't load this batch.</p>
      <Button variant="outline" className="mt-4 rounded-full" onClick={() => nav(-1)}>Go back</Button>
    </div>
  );
  if (!data) return (
    <div className="grid place-items-center py-24" data-testid="batch-room-loading"><CircleNotch size={26} className="animate-spin text-primary" /></div>
  );

  const legacyUrl = `${EDITION.url}/institutions/${instId}/batch/${year}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`Calling the Class of ${year}, ${instName}! Reconnect with our batch on ${EDITION.brand}: ${legacyUrl}`)}`;

  return (
    <div className="bg-background" data-testid="batch-room">
      <div className="border-b border-border/60 bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="mx-auto max-w-4xl px-5 py-8">
          <button onClick={() => nav(`/institutions/${instId}`)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="batch-back">
            <ArrowLeft size={16} /> {instName || "Institution"}
          </button>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-primary/70"><GraduationCap size={16} weight="fill" /> Batch Room</p>
              <h1 className="mt-1 font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="batch-title">Class of {year}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <Link to={`/institutions/${instId}`} className="text-primary hover:underline">{instName}</Link>
                <span className="flex items-center gap-1"><UsersThree size={15} /> {data.member_count} {data.member_count === 1 ? "classmate" : "classmates"}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="batch-whatsapp">
                <Button size="sm" variant="outline" className="gap-1.5 rounded-full border-[#25D366]/40 text-[#128C4A] hover:bg-[#25D366]/10"><WhatsappLogo size={15} weight="fill" /> Invite the batch</Button>
              </a>
              <Link to={`/events?institution=${encodeURIComponent(instName)}&grad_year=${year}`} data-testid="batch-reunion">
                <Button size="sm" className="gap-1.5 rounded-full"><CalendarPlus size={15} weight="fill" /> Plan a reunion</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl space-y-8 px-5 py-8">
        {/* Members */}
        <section data-testid="batch-members">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg"><UsersThree size={20} weight="duotone" className="text-primary" /> Batchmates</h2>
          {data.members.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/70 bg-white p-8 text-center">
              <p className="text-sm text-muted-foreground">No one from this batch has joined yet. Be the first — invite your classmates!</p>
              <a href={waHref} target="_blank" rel="noreferrer" className="mt-3 inline-block"><Button size="sm" className="gap-1.5 rounded-full"><WhatsappLogo size={15} weight="fill" /> Invite on WhatsApp</Button></a>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {data.members.map((m) => (
                <Link key={m.user_id} to={`/u/${m.public_slug || m.user_id}`} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-white p-4 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`batch-member-${m.user_id}`}>
                  {m.avatar_file_id ? <img src={fileUrl(m.avatar_file_id)} alt="" className="h-11 w-11 rounded-full object-cover" /> : <div className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary"><UserCircle size={26} weight="duotone" /></div>}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{m.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{[m.headline, m.course].filter(Boolean).join(" · ") || m.location}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Teachers of the era */}
        {data.teachers.length > 0 && (
          <section data-testid="batch-teachers">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg"><ChalkboardTeacher size={20} weight="duotone" className="text-primary" /> Teachers of your era</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {data.teachers.map((t) => (
                <Link key={t.id} to={`/person/${t.id}`} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-white p-4 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`batch-teacher-${t.id}`}>
                  {t.photo_url ? <img src={t.photo_url.startsWith("http") ? t.photo_url : fileUrl(t.photo_url)} alt="" className="h-11 w-11 rounded-full object-cover" /> : <div className="grid h-11 w-11 place-items-center rounded-full bg-secondary/10 text-secondary"><ChalkboardTeacher size={22} weight="duotone" /></div>}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{t.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{t.title || "Teacher"}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Batch discussion */}
        <section data-testid="batch-discussion-section">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg"><GraduationCap size={20} weight="duotone" className="text-primary" /> Batch wall</h2>
          <SiteDiscussion boardType="batch" boardId={data.discussion_id} listUrl={`/public/batch/${instId}/${year}/discussion`} t={THEME} placeholder={`Share a memory with the Class of ${year}…`} />
        </section>
      </div>
    </div>
  );
}
